/**
 * Pushing a won enquiry's chain to G6 (Akhil, 2026-10-16: "add api to confirm when quotation
 * is won in the requirement page").
 *
 * The connector's `push_enquiry_chain {enquiry_no}` sends Enquiry → Supplier Quotation →
 * Supplier Quote Comparison → Quotation to every active connector (collection
 * `Quoflow_Enquiry_Chain.postman_collection.json`; `step` left blank = all four). It is the
 * second method outside `quoflow.*` the app calls — `uomSync.ts`'s `pull_item_uom` was the
 * first — so the proxy allows it by exact name on POST alone (`server/policy.ts`).
 *
 * Best effort, AFTER the decision: a push that fails must never read as a Won that did not
 * happen. The decision is already saved server-side; the warning tells the purchaser the push
 * is what is missing, and the server's own sync flag (`eligible_for_sync`) still stands.
 */
import { useMutation } from '@tanstack/react-query'
import { postDotted } from './bosun'

export const PUSH_ENQUIRY_CHAIN_METHOD = 'quoflow_connector.quoflow_connector.api.push_enquiry_chain'

type Post = <T>(dotted: string, body?: unknown) => Promise<T>

/** `null` when the push went through; otherwise one sentence for the toast. */
export async function pushEnquiryChain(enquiryId: string, post: Post = postDotted): Promise<string | null> {
  try {
    await post<unknown>(PUSH_ENQUIRY_CHAIN_METHOD, { enquiry_no: enquiryId })
    return null
  } catch (error) {
    // "Sync", not "push to G6", on screen (Akhil, 2026-10-16) — the connector is ours to name, not theirs.
    return `the ERP sync failed: ${error instanceof Error ? error.message : String(error)}`
  }
}

/**
 * The manual Sync on the Requirements page (2026-10-24) — the same call the Won makes, offered
 * again for a chain the ERP did not receive.
 *
 * `pushEnquiryChain` reports failure as a warning string rather than throwing, so this mutation
 * never errors: `onSettled` fires with the warning, or `null` when it went through. Keeping that
 * shape means the button and the Won share one notion of "what happened", and the toast wording
 * lives in one place (`lib/wonDecision.ts`'s `syncToast`).
 */
export function useSyncEnquiryChain(options?: { onDone?: (enquiryId: string, warning: string | null) => void }) {
  return useMutation<string | null, Error, string>({
    mutationFn: (enquiryId) => pushEnquiryChain(enquiryId),
    onSuccess: (warning, enquiryId) => options?.onDone?.(enquiryId, warning),
  })
}
